<template>
  <div class="date-time-input">
    <div class="icon-group input-group">
      <span class="input-group-btn">
        <button class="btn btn-primary" type="button" :disabled="disabled" v-on:click="togglePicker">
          <i class="fa fa-clock-o"></i>
        </button>
      </span>
      <input
        :id="id"
        :name="name"
        :data-testid="dataTestid"
        :disabled="disabled"
        :class="inputClass"
        type="text"
        :value="text"
        v-on:input="onInput"
        v-on:blur="onBlur"
        v-on:keydown.enter="onEnter"
      />
    </div>
    <!--
      Replaces `moment-picker`. The text input stays the surface the booking specs drive
      (`.fill()` + a blur); the picker is the button affordance beside it.
    -->
    <div class="date-time-input-picker" v-if="open">
      <DatePicker
        mode="dateTime"
        :model-value="dateValue"
        :first-day-of-week="2"
        is-dark
        v-on:update:model-value="onPick"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { DatePicker } from 'v-calendar'

export default {
  name: 'DateTimeInput',
  // Declared so the parent's `v-on:change` listens for the component's emit, not the native
  // `change` bubbling from the inner <input> to the root (Vue 3 listener fallthrough).
  emits: ['change'],
  components: { DatePicker },
  props: {
    // The formatted date/time string. A Date is also accepted because the controller swaps
    // startsAt/endsAt to Date objects inside save().
    value: {
      type: [String, Date],
      default: '',
    },
    id: {
      type: String,
      default: undefined,
    },
    name: {
      type: String,
      default: undefined,
    },
    dataTestid: {
      type: String,
      default: undefined,
    },
    inputClass: {
      type: String,
      default: 'form-control',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    format: {
      type: String,
      default: 'DD-MM-YYYY HH:mm',
    },
  },
  data() {
    return {
      text: this.displayText(this.value),
      open: false,
    }
  },
  computed: {
    dateValue() {
      const parsed = moment(this.text, this.format, true)
      return parsed.isValid() ? parsed.toDate() : undefined
    },
  },
  watch: {
    value(value) {
      this.text = this.displayText(value)
    },
  },
  methods: {
    displayText(value) {
      if (!value) {
        return ''
      }
      return value instanceof Date ? moment(value).format(this.format) : value
    },
    onInput(event) {
      this.text = event.target.value
    },
    onBlur() {
      this.commit()
    },
    onEnter(event) {
      // Enter should apply the value like a blur does, without submitting the surrounding form.
      event.preventDefault()
      event.target.blur()
    },
    commit() {
      this.$emit('change', this.text)
    },
    togglePicker() {
      this.open = !this.open
    },
    closePicker() {
      this.open = false
    },
    onPick(date) {
      this.text = moment(date).format(this.format)
      this.open = false
      this.$emit('change', this.text)
    },
  },
}
</script>

<style scoped>
.date-time-input {
  position: relative;
}
.date-time-input-picker {
  position: absolute;
  z-index: 1000;
  margin-top: 4px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.25);
}
</style>
